/** @jsxRuntime classic */
/** @jsx jsx */

import { jsx } from '@emotion/core';
import { css } from '@emotion/react';
import { labelStyles } from '@zapier/style-encapsulation';
import { Colors, Typography } from '@zapier/design-system';
import { MEDIA_MIN_WIDTH } from '../../../constants';
import { RichTextHTMLString } from '../types';
import { ArrowLink } from '../../ArrowLink/ArrowLink';
import { useAvroTracking, CTAType, SectionName } from '../../../observability';
import { ReactNode } from 'react';
import { RichTextHtml } from '../../RichTextHtml/RichTextHtml';

type Props = {
  children: ReactNode;
  heading: string;
  details: RichTextHTMLString;
  href: string;
  linkText: string;
  disableSpacing?: boolean;
};

const Styles = labelStyles('TwoColumnSection', {
  root: (disableSpacing?: boolean) =>
    css({
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      margin: '0 auto',
      maxWidth: '1210px',
      padding: disableSpacing ? '0' : '40px 20px',
      [MEDIA_MIN_WIDTH.small]: {
        flexDirection: 'row',
        padding: disableSpacing ? '0' : '48px 20px',
      },

      [MEDIA_MIN_WIDTH.large]: {
        padding: disableSpacing ? '0' : '80px 20px',
      },
    }),
  imageContainer: css({
    marginBottom: '32px',
    width: '100%',

    [MEDIA_MIN_WIDTH.small]: {
      margin: 'auto 0',
      paddingRight: '32px',
      width: '50%',
    },

    [MEDIA_MIN_WIDTH.large]: {
      paddingRight: '131px',
    },
  }),
  contentContainer: css({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    boxSizing: 'border-box',

    [MEDIA_MIN_WIDTH.small]: {
      width: '50%',
    },

    [MEDIA_MIN_WIDTH.large]: {
      paddingRight: '40px',
    },
  }),
  heading: css({
    color: Colors.earth,
    lineHeight: '110%',
    fontSize: '36px',
    fontWeight: 600,
    paddingBottom: '24px',
    ...Typography.heading,

    [MEDIA_MIN_WIDTH.small]: {
      fontSize: '32px',
      paddingBottom: '16px',
    },

    [MEDIA_MIN_WIDTH.large]: {
      fontSize: '60px',
      paddingBottom: '40px',
    },
  }),
  details: css({
    color: Colors.neutral800,
    lineHeight: '24px',
    fontFamily: Typography.base.fontFamily,
    fontWeight: 400,
    fontSize: '16px',
    paddingBottom: '24px',
    width: '75%',

    [MEDIA_MIN_WIDTH.small]: {
      lineHeight: '24px',
      fontSize: '18px',
      paddingBottom: '16px',
      width: '100%',
    },

    [MEDIA_MIN_WIDTH.large]: {
      lineHeight: '28px',
      fontSize: '20px',
      paddingBottom: '32px',
    },
  }),
});

export const TwoColumnSection = (props: Props) => {
  const { emitCTAClickEvent } = useAvroTracking();
  return (
    <div css={Styles.root(props.disableSpacing)}>
      <div css={Styles.imageContainer}>{props.children}</div>
      <div css={Styles.contentContainer}>
        <h2 css={Styles.heading}>{props.heading}</h2>
        <div css={Styles.details}>
          <RichTextHtml html={props.details} />
        </div>
        {props.linkText && (
          <ArrowLink
            href={props.href}
            onClick={() =>
              emitCTAClickEvent({
                text: props.linkText,
                href: props.href,
                type: CTAType.cta_link,
                section: SectionName.TwoColumnSection,
              })
            }
          >
            {props.linkText}
          </ArrowLink>
        )}
      </div>
    </div>
  );
};
